import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BROKER_SYNC_RUN_STATUS_OBJ, PROMISE_SETTLED_STATUS_OBJ } from "@signals/shared";
import { qk, useApiQuery } from "../_common";
import type { BrokerSyncAccountsResponse } from "./broker-sync-panel.types";
import { successfulBrokerCheckAt } from "./portfolio-freshness.util";

const BROKER_SYNC_STATUS_POLL_INTERVAL_MS = 30_000;

export function useBrokerSyncAccounts(portfolioId: string) {
  return useApiQuery<BrokerSyncAccountsResponse>(
    qk.portfolio.brokerSync.accounts(portfolioId),
    `/api/portfolio/broker-sync/accounts?portfolioId=${encodeURIComponent(portfolioId)}`,
    { enabled: !!portfolioId, refetchInterval: BROKER_SYNC_STATUS_POLL_INTERVAL_MS },
  );
}

/** Reconcile each successful import with the figures once per QueryClient, including cached views. */
export function useRefreshPortfolioAfterBrokerSync(portfolioId: string, data: BrokerSyncAccountsResponse | undefined, receivedAt: number) {
  const client = useQueryClient();
  useEffect(() => {
    if (!data || !portfolioId) return;
    const successes = data.items.flatMap(account => {
      const time = successfulBrokerCheckAt(account);
      if (!time) return [];
      const id = account.lastSuccessfulRun?.id
        ?? (account.latestRun?.status === BROKER_SYNC_RUN_STATUS_OBJ.SUCCEEDED ? account.latestRun.id : undefined);
      return [[account.accountId, id ?? time]];
    }).sort(([left], [right]) => String(left).localeCompare(String(right)));
    if (successes.length === 0) return;
    const signature = JSON.stringify(successes);
    const reconciledKey = qk.portfolio.brokerSync.reconciled(portfolioId);
    // Keep the watermark with this QueryClient, across view unmounts and portfolio switches.
    client.setQueryDefaults(reconciledKey, { gcTime: Infinity });
    if (client.getQueryData<string>(reconciledKey) === signature) return;
    client.setQueryData(reconciledKey, signature);
    void Promise.allSettled([
      qk.portfolio.summaryForPortfolio(portfolioId),
      qk.portfolio.positionsForPortfolio(portfolioId),
      qk.portfolio.transactionsForPortfolio(portfolioId),
      qk.portfolio.positionHistoryForPortfolio(portfolioId),
      qk.portfolio.cashflowsForPortfolio(portfolioId),
      qk.portfolio.incomeEvents.forPortfolio(portfolioId),
      qk.portfolio.accountsForPortfolio(portfolioId),
      qk.portfolio.brokerSync.issuesForPortfolio(portfolioId),
      qk.portfolio.compareForPortfolio(portfolioId),
      qk.portfolio.yearlyPerformance.all(portfolioId),
      qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId),
      qk.portfolio.snapshots(portfolioId),
      qk.portfolio.snapshotForPortfolio(portfolioId),
      qk.portfolio.snapshotRevisionsForPortfolio(portfolioId),
    ].map(async queryKey => {
      // Cancel an older in-flight read too: its completion time is not its ledger-read time.
      await client.cancelQueries({ queryKey });
      await client.invalidateQueries({ queryKey }, { throwOnError: true });
    })).then(results => {
      if (results.some(result => result.status === PROMISE_SETTLED_STATUS_OBJ.REJECTED) && client.getQueryData<string>(reconciledKey) === signature) {
        client.removeQueries({ queryKey: reconciledKey, exact: true });
      }
    });
  }, [client, data, portfolioId, receivedAt]);
}
